<template>
    <div
        class="avoid-page-break-inside"
        data-test-role="inverter-div">
        <hr class="m-0 mb-3" />
        <pc-alert
            v-bind="programDoesNotSupportEssInvertersBinder"
            data-test-role="program-excludes-ess-inverters-warning"
            class="mt-2 mb-0 col-9">
            {{ programDoesNotSupportEssInvertersMessage }}
        </pc-alert>
        <pc-alert
            v-bind="pvSystemGroupDoesNotSupportEssInvertersBinder"
            data-test-role="pvsystem-ess-inverters-warning"
            class="mt-2 mb-0 col-9">
            {{ pvSystemGroupDoesNotSupportEssInvertersMessage }}
        </pc-alert>
        <pc-alert
            v-bind="projectDoesNotMeetEssInverterConditionWarningBinder"
            data-test-role="project-ess-inverter-condition-warning"
            class="mt-2 mb-0 col-9">
            {{ projectDoesNotMeetEssInverterConditionWarningMessage }}
        </pc-alert>
        <fieldset class="mb-3">
            <legend class="h6 mb-0">
                <span class="d-inline-flex align-items-baseline col-12 col-lg-11 mb-0">
                    <div class="mb-2">
                        <span>{{ inverterLabel }}</span>
                        <pc-button
                            v-if="showDeleteInverterButton"
                            v-bind:disabled="readonly"
                            class="mx-4 p-0 border-0"
                            style="line-height: inherit"
                            data-test-role="delete-inverter"
                            link
                            v-on:click="onDelete">
                            {{ deleteInverterButtonLabel }}
                        </pc-button>
                        <span style="display: inline-block; width: 32px">&nbsp;</span>
                        <span
                            v-if="useEnhancedUi && useInverterSelectorToggles"
                            class="d-inline-flex">
                            <pc-checkbox
                                v-model="includeSolarOnlyInverters"
                                v-bind:disabled="readonly"
                                v-bind:label="inverterTypeSolarOnlyLabel"
                                class="mx-2"
                                style="line-height: inherit"
                                switch
                                v-on:update:model-value="onUpdateInverterTypes">
                            </pc-checkbox>
                            <pc-checkbox
                                v-model="includeHybridInverters"
                                v-bind:disabled="readonly"
                                v-bind:label="inverterTypeHybridLabel"
                                class="mx-2"
                                style="line-height: inherit"
                                switch
                                v-on:update:model-value="onUpdateInverterTypes">
                            </pc-checkbox>
                            <pc-checkbox
                                v-model="includeEssInverters"
                                v-bind:disabled="readonly"
                                v-bind:label="inverterTypeEssLabel"
                                class="mx-2"
                                style="line-height: inherit"
                                switch
                                v-on:update:model-value="onUpdateInverterTypes">
                            </pc-checkbox>
                        </span>
                    </div>
                    <div
                        v-if="!readonly"
                        class="ms-auto">
                        <pc-loader
                            small
                            v-bind:loading="copyingSubsystem">
                            <pc-button-icon
                                v-bind:disabled="copyingSubsystem"
                                v-bind:link="cloneButtonConfig.link"
                                v-bind:style="cloneButtonConfig.style"
                                v-bind:readonly="readonly"
                                v-bind:icon="cloneButtonConfig.iconName"
                                v-on:click="onCopySubsystemClicked">
                                {{ subsystemCloneButtonLabel }}
                            </pc-button-icon>
                        </pc-loader>
                    </div>
                </span>
            </legend>
            <pc-alert
                v-bind="showInverterSmartStandardInfoBinder"
                data-test-role="inverter-smart-standard-info"
                class="mt-2 mb-0 col-9">
                {{ showInverterSmartStandardInfoMessage }}
            </pc-alert>
            <model-picker
                ref="modelPicker"
                class="mb-3"
                v-bind:manufacturers="inverterManufacturers"
                v-bind:load-models="loadInverterModels"
                v-bind:dynamic-model-filter="filterInverterModelsByType"
                v-bind:spec="subsystem"
                v-bind:equipment-type="equipmentType"
                v-bind:use-enhanced-ui="useEnhancedUi"
                v-bind:model-descriptor="inverterDescriptor(selectedModel)"
                v-bind:can-copy="canCopy"
                v-bind:readonly="readonly"
                v-on:copy-subsystem="onCopySubsystemClicked"
                v-on:copy-component="onCopyInverterClicked">
            </model-picker>
            <div v-bind:class="responsiveClasses.subsystemWrapper">
                <transition-group
                    tag="span"
                    v-bind:css="false"
                    v-on:enter="enter"
                    v-on:leave="leave">
                    <moduleGroup
                        v-for="pvModule in subsystem.moduleGroups"
                        v-bind:id="pvModule.id"
                        v-bind:ref="pvModule.id"
                        v-bind:key="pvModule.id"
                        v-bind:pv-module="pvModule"
                        v-bind:parent-id="subsystem.id"
                        v-bind:readonly="readonly"
                        v-on:copy-modulegroup="copyModuleGroup" />
                </transition-group>
                <validation-render-helper
                    v-if="!hasModuleGroups"
                    v-bind:validation-error="[inverterRequiresPVArrayErrorMessage]"
                    class="mt-0" />
                <pc-button
                    class="mb-2"
                    v-bind:disabled="readonly"
                    data-test-role="add-array-btn"
                    v-on:click="onAddEmptyModule">
                    {{ addArrayButtonLabel }}
                </pc-button>
            </div>
            <div
                v-if="showBatteryComponent"
                v-bind:class="responsiveClasses.subsystemWrapper">
                <pc-alert
                    v-bind="invalidBatterySelectionWarningBinder"
                    data-test-role="invalid-battery-selection-warning"
                    class="mt-2 col-9">
                    {{ invalidBatterySelectionWarningMessage }}
                </pc-alert>
                <pc-alert
                    v-bind="projectDoesNotMeetStorageConditionWarningBinder"
                    data-test-role="project-storage-condition-warning"
                    class="mt-2 col-9">
                    {{ projectDoesNotMeetStorageConditionWarningMessage }}
                </pc-alert>
                <transition-group
                    tag="span"
                    v-bind:css="false"
                    v-on:enter="enter"
                    v-on:leave="leave">
                    <equipmentGroup
                        v-for="equipmentItem in subsystem.equipmentGroups"
                        v-bind:id="equipmentItem.id"
                        v-bind:ref="equipmentItem.id"
                        v-bind:key="equipmentItem.id"
                        v-bind:equipment-item="equipmentItem"
                        v-bind:equipment-type="equipmentItem.equipmenttype"
                        v-bind:parent-id="subsystem.id"
                        v-bind:can-copy="canAddStorage"
                        v-bind:readonly="readonly"
                        v-on:copy-equipmentgroup="copyEquipmentGroup" />
                </transition-group>
                <transition
                    v-bind:css="false"
                    v-on:enter="enter"
                    v-on:leave="leave">
                    <div v-if="showAddBatteryButton">
                        <pc-button
                            class="mb-2"
                            v-bind:disabled="!canAddStorage"
                            data-test-role="add-battery-btn"
                            v-on:click="onAddEmptyBattery">
                            {{ addBatteryButtonLabel }}
                        </pc-button>
                        <pc-help-icon
                            v-if="showAddBatteryHelpButton"
                            data-test-role="add-battery-help-icon"
                            class="ms-2"
                            v-on:click="showAddBatteryHelpMessage = !showAddBatteryHelpMessage" />
                    </div>
                </transition>
                <transition
                    v-bind:css="false"
                    v-on:enter="enter"
                    v-on:leave="leave">
                    <pc-alert
                        v-if="showAddBatteryHelpButton"
                        v-model:shown="showAddBatteryHelpMessage"
                        data-test-role="add-battery-help-message"
                        class="mt-2 col-9"
                        dismissible>
                        {{ addBatteryButtonIsDisabledHelp }}
                    </pc-alert>
                </transition>
            </div>
        </fieldset>
    </div>
</template>

<script>
    import moduleGroup from "./moduleGroup.vue";
    import equipmentGroup from "./equipmentGroup.vue";
    import modelPicker from "./modelPicker.vue";
    import validationRenderHelper from "Validation/_validation-error-helper.vue";
    import { SLIDE_DOWN_UP } from "Util/animations";
    import { ALERT_SEVERITY } from "Util/alerts";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./subsystem.i18n";
    import { extendManufacturerList, extendModelList } from "./_modelsUtil";
    import { COMPONENT_TYPE } from "Types/componentTypes";
    import { getResponsiveClassMap } from "Responsive/manager";

    const responsiveConfig = {
        subsystemWrapper: {
            ms: { xs: 2, sm: 5 }
        }
    };

    export default {
        components: { modelPicker, moduleGroup, equipmentGroup, validationRenderHelper },
        inject: {
            pvSystemElement: {
                from: "pvSystemElement"
            },
            viewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            subsystem: { type: Object, required: true },
            readonly: { type: Boolean, required: false, default: false }
        },
        emits: ["copy-subsystem", "copy-inverter"],
        data: function () {
            const cloneButtonConfig = {
                link: true,
                style: {
                    textDecoration: "none"
                },
                iconName: "copy"
            };
            return {
                equipmentType: COMPONENT_TYPE.Inverter,
                includeSolarOnlyInverters: true,
                includeHybridInverters: true,
                filteredModelList: [],
                copyingSubsystem: false,
                showAddBatteryHelpMessage: false,
                cloneButtonConfig,
                MODEL_ATTRIBUTES_INVERTER_TYPE: {
                    Solar: 1,
                    Hybrid: 3,
                    ESS: 4
                }
            };
        },
        computed: {
            hasInverterFiltering() {
                const filtering = this.pvSystemElement.element.Properties.inverterlistfiltering;
                return filtering !== "0" && Boolean(filtering);
            },
            includeEssInverters() {
                return this.showStorageEquipmentConditionMet;
            },
            inverterManufacturers() {
                const invManufList = this.$serviceControllers.PvSystem.getInverterManufacturers() || [];

                if (this.subsystem && this.subsystem.manufacturerId && parseInt(this.subsystem.manufacturerId) >= 0) {
                    const clearManufId = extendManufacturerList(
                        invManufList,
                        this.subsystem.manufacturerId,
                        this.pvSystemElement.projectElement.AuxiliaryModel.storedPvModels
                    );

                    if (clearManufId) {
                        this.pvSystemElement.setInverterProperty({
                            subsystemId: this.subsystem.id,
                            name: "manufacturerId",
                            value: ""
                        });
                    }
                }

                return invManufList;
            },
            useEnhancedUi() {
                return this.showBatteryComponent && this.pvSystemElement.element.AuxiliaryModel.programUsesEssInverters;
            },
            // Inverter selector toggles are being removed from the initial MVP release;
            //  leaving the code in place for now in case they are restored later
            useInverterSelectorToggles() {
                return false;
            },
            selectedModel() {
                if (this.subsystem.modelId) {
                    return this.filteredModelList.find((model) => model.ModelId === parseInt(this.subsystem.modelId));
                }
                return null;
            },
            showInverterSmartStandardInfo() {
                return (
                    this.hasInverterFiltering &&
                    this.selectedModel &&
                    this.selectedModel.Smart &&
                    this.selectedModel.Smart.length > 0
                );
            },
            selectedModelIsSolarOnlyInverter() {
                return this.inverterType(this.selectedModel) === this.inverterTypeSolarOnlyLabel;
            },
            selectedModelIsEssInverter() {
                return this.isEssInverterModel(this.selectedModel);
            },
            showBatteryComponent() {
                // This has the top-level setting for this PV system element; it may be further
                //  restricted by a formula; see showStorageEquipmentConditionsMet().
                return this.pvSystemElement.element.AuxiliaryModel.elementShowsBatteryComponent;
            },
            showStorageEquipmentConditionMet() {
                // pvShowStorageEquipment is the value of the "show" formula for storage equipment,
                //  or true if there isn't a formula-based condition
                if (this.pvSystemElement.element.AuxiliaryModel.showStorageEquipmentFormulaId) {
                    return this.pvSystemElement.projectElement.AuxiliaryModel.pvShowStorageEquipment;
                }
                return true;
            },
            projectDoesNotMeetStorageCondition() {
                return this.showBatteryComponent && !this.showStorageEquipmentConditionMet;
            },
            projectDoesNotMeetEssInverterCondition() {
                return this.isEssInverterModel(this.selectedModel) && this.projectDoesNotMeetStorageCondition;
            },
            canCopy() {
                return !this.projectDoesNotMeetEssInverterCondition;
            },
            canAddStorage() {
                if (this.readonly) {
                    return false;
                } else if (this.useEnhancedUi && (!this.selectedModel || this.selectedModelIsSolarOnlyInverter)) {
                    return false;
                } else if (this.projectDoesNotMeetStorageCondition) {
                    return false;
                }
                return true;
            },
            showAddBatteryButton() {
                return (
                    this.pvSystemElement.element.GroupDataFields.hasOwnProperty("Battery Quantity") &&
                    this.showStorageEquipmentConditionMet
                );
            },
            showAddBatteryHelpButton() {
                return this.useEnhancedUi && this.showAddBatteryButton && this.selectedModelIsSolarOnlyInverter;
            },
            hasModuleGroups() {
                return this.subsystem && this.subsystem.moduleGroups && this.subsystem.moduleGroups.length > 0;
            },
            hasEquipmentGroups() {
                return this.subsystem && this.subsystem.equipmentGroups && this.subsystem.equipmentGroups.length > 0;
            },
            invalidBatterySelection() {
                return this.useEnhancedUi && this.selectedModelIsSolarOnlyInverter && this.hasEquipmentGroups;
            },
            invalidBatterySelectionWarningBinder() {
                return {
                    shown: this.invalidBatterySelection,
                    severity: ALERT_SEVERITY.info
                };
            },
            programDoesNotSupportEssInvertersBinder() {
                return {
                    shown:
                        this.selectedModelIsEssInverter &&
                        !this.pvSystemElement.element.AuxiliaryModel.programUsesEssInverters,
                    severity: ALERT_SEVERITY.warning
                };
            },
            pvSystemGroupDoesNotSupportEssInvertersBinder() {
                return {
                    shown: this.selectedModelIsEssInverter && !this.showBatteryComponent,
                    severity: ALERT_SEVERITY.warning
                };
            },
            projectDoesNotMeetEssInverterConditionWarningBinder() {
                return {
                    shown: this.projectDoesNotMeetEssInverterCondition,
                    severity: ALERT_SEVERITY.warning
                };
            },
            projectDoesNotMeetStorageConditionWarningBinder() {
                return {
                    shown: this.hasEquipmentGroups && this.projectDoesNotMeetStorageCondition,
                    severity: ALERT_SEVERITY.warning
                };
            },
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            },
            showDeleteInverterButton() {
                return (
                    this.pvSystemElement.subsystems.length > 1 &&
                    this.subsystem.moduleGroups.length <= 0 &&
                    !(this.showBatteryComponent && this.subsystem.equipmentGroups.length > 0)
                );
            },
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        watch: {
            selectedModel() {
                this.showAddBatteryHelpMessage = false;
            },
            async showStorageEquipmentConditionMet() {
                this.$refs.modelPicker.dynamicallyFilterModels(true);
            }
        },
        methods: {
            // This function is passed to the modelPicker component and executed from there
            async loadInverterModels(manufacturerId) {
                const invModelList =
                    (await this.$serviceControllers.PvSystem.getInverterModels(
                        manufacturerId,
                        this.showBatteryComponent,
                        this.pvSystemElement.element.Properties.inverterlistfiltering
                    )) || [];

                if (this.subsystem && this.subsystem.modelId && parseInt(this.subsystem.modelId) >= 0) {
                    extendModelList(
                        invModelList,
                        this.subsystem.modelId,
                        this.pvSystemElement.projectElement.AuxiliaryModel.storedPvModels,
                        this.updatedModelSuffix
                    );
                }

                return invModelList;
            },
            // This function is passed to the modelPicker component and executed from there
            filterInverterModelsByType(fullModelList, currentModelId) {
                this.filteredModelList = [];
                if (this.includeSolarOnlyInverters && this.includeHybridInverters && this.includeEssInverters) {
                    this.filteredModelList = fullModelList;
                } else {
                    this.filteredModelList = fullModelList.filter((model) => {
                        return (
                            // Grandfather in the current selection
                            // (coerce because model.ModelId is a number, but currentModelId is a string)
                            model.ModelId == currentModelId ||
                            // Filter by the selected inverter types
                            (this.includeSolarOnlyInverters &&
                                this.inverterType(model) === this.inverterTypeSolarOnlyLabel) ||
                            (this.includeHybridInverters &&
                                this.inverterType(model) === this.inverterTypeHybridLabel) ||
                            (this.includeEssInverters && this.inverterType(model) === this.inverterTypeEssLabel)
                        );
                    });
                }
                return this.filteredModelList;
            },
            onUpdateInverterTypes() {
                this.$refs.modelPicker.dynamicallyFilterModels(false);
                return;
            },
            hasAttribute(model, attribute) {
                return (model.ModelAttributes & attribute) === attribute;
            },
            isEssInverterModel(model) {
                if (model) {
                    return this.hasAttribute(model, this.MODEL_ATTRIBUTES_INVERTER_TYPE.ESS);
                }
                return false;
            },
            inverterType(model) {
                // Note: The order of the if statements is significant:  A Hybrid inverter is also a Solar inverter,
                //  so check Hybrid first:  if the model is not a Hybrid, but is a Solar, it is a Solar-Only inverter.
                //  An ESS inverter is not a Solar inverter, so it's not affected by the other two attributes.
                if (model) {
                    if (this.hasAttribute(model, this.MODEL_ATTRIBUTES_INVERTER_TYPE.Hybrid)) {
                        return this.inverterTypeHybridLabel;
                    } else if (this.hasAttribute(model, this.MODEL_ATTRIBUTES_INVERTER_TYPE.Solar)) {
                        return this.inverterTypeSolarOnlyLabel;
                    } else if (this.hasAttribute(model, this.MODEL_ATTRIBUTES_INVERTER_TYPE.ESS)) {
                        return this.inverterTypeEssLabel;
                    }
                }
                return "";
            },
            // The inverter descriptor shows the type and also that it is a microinverter if it is one
            inverterDescriptor(model) {
                if (model) {
                    let descriptor = this.inverterType(model);
                    if (descriptor === this.inverterTypeHybridLabel) {
                        if (model.IsMicroinverter) {
                            descriptor = this.hybridMicroinverterDescriptor;
                        }
                    } else if (descriptor === this.inverterTypeSolarOnlyLabel) {
                        if (model.IsMicroinverter) {
                            descriptor = this.solarOnlyMicroinverterDescriptor;
                        }
                    } else if (descriptor !== this.inverterTypeEssLabel) {
                        // If it's an ESS inverter, the descriptor is just the type (there are no ESS microinverters);
                        //  if it's not an ESS inverter, just return an empty string
                        descriptor = "";
                    }
                    return descriptor;
                }
                return "";
            },
            onAddEmptyModule() {
                this.pvSystemElement.addEmptyModule(this.subsystem.id);
            },
            onAddEmptyBattery() {
                this.pvSystemElement.addEmptyEquipmentItem(this.subsystem.id, COMPONENT_TYPE.Battery);
            },
            onDelete() {
                this.pvSystemElement.deleteSubsystem(this.subsystem.id);
            },
            async onCopySubsystemClicked() {
                this.copyingSubsystem = true;
                await this.$app.timeout(0);
                await this.$emit(
                    "copy-subsystem",
                    this.subsystem.id,
                    this.canAddStorage,
                    this.isEssInverterModel(this.selectedModel)
                );
                this.copyingSubsystem = false;
            },
            async onCopyInverterClicked() {
                await this.$emit("copy-inverter", this.subsystem.id);
            },
            async copyModuleGroup(moduleGroupId) {
                const newPvModuleId = await this.pvSystemElement.copyModuleGroup(this.subsystem.id, moduleGroupId);
                // Put in a break to allow the UI to update with the ref for the new module
                await this.$app.timeout(0);
                // Set the focus to the new module
                if (this.$refs[newPvModuleId]) {
                    this.$refs[newPvModuleId][0].focusModuleGroupInput();
                }
            },
            async copyEquipmentGroup(equipmentGroupId) {
                const newEquipmentId = await this.pvSystemElement.copyEquipmentGroup(
                    this.subsystem.id,
                    equipmentGroupId
                );
                // Put in a break to allow the UI to update with the ref for the new module
                await this.$app.timeout(0);
                // Set the focus to the new equipment group
                if (this.$refs[newEquipmentId]) {
                    this.$refs[newEquipmentId][0].focusEquipmentGroupInput();
                }
            },
            focusSubsystemInput() {
                this.$refs.modelPicker.setFocusToQuantityInput();
            },
            enter: SLIDE_DOWN_UP.enter,
            leave: SLIDE_DOWN_UP.leave
        }
    };
</script>
